import {
  areSolutionsVisible as areTimelineSolutionsVisible,
  getComponentTimeline,
  getSolutionsInterval,
  getSolutionVisibilityTimestamp as getTimelineSolutionVisibilityTimestamp,
  parseTimestamp,
} from '@/features/content/hooks/useComponentTimeline';
import type { CourseQuestionDefaults, RawComponent } from '@/features/content/useComponent';

export interface ComponentTimingState {
  endTimeUtcMilli?: number;
  isLive: boolean;
  isPastDue: boolean;
  isPreLecturePastDue: boolean;
  preLectureEndTimeUtcMilli?: number;
  preLectureSolutionsVisible: boolean;
  preLectureSolutionsVisibleAtUtcMilli?: number;
  solutionsVisible: boolean;
  solutionsVisibleAtUtcMilli?: number;
}

function normalizeBoolean(value: unknown): boolean | undefined {
  return typeof value === 'boolean' ? value : undefined;
}

function getTimestampMilliseconds(value: unknown): number | undefined {
  return parseTimestamp(value) ?? undefined;
}

export function getComponentEndTimeUtcMilli(component: RawComponent) {
  return getTimestampMilliseconds(
    component.end_time_utc_milli ?? component.endTimeUtcMilli,
  );
}

export function getPreLectureEndTimeUtcMilli(component: RawComponent) {
  return getTimestampMilliseconds(
    component.pre_lecture_end_time_utc_milli ??
      component.preLectureEndTimeUtcMilli,
  );
}

export function getSolutionVisibilityTimestamp(
  endTimeUtcMilli: number | undefined,
  interval: number | undefined,
): number | undefined {
  return getTimelineSolutionVisibilityTimestamp(endTimeUtcMilli ?? null, interval);
}

export function areSolutionsVisible(
  now: number,
  endTimeUtcMilli: number | undefined,
  interval: number | undefined,
) {
  return areTimelineSolutionsVisible(now, endTimeUtcMilli ?? null, false, interval);
}

// courseDefaults are the course's question settings, for a chapter that leaves
// when solutions show to its course.
export function getComponentTimingState(
  component: RawComponent,
  now = Date.now(),
  courseDefaults?: CourseQuestionDefaults,
): ComponentTimingState {
  const timeline = getComponentTimeline(component, now);
  const endTimeUtcMilli = getComponentEndTimeUtcMilli(component);
  const preLectureEndTimeUtcMilli = getPreLectureEndTimeUtcMilli(component);
  const homeworkInterval = getSolutionsInterval(component, courseDefaults, 'homework');
  const preLectureInterval = getSolutionsInterval(component, courseDefaults, 'preLecture');
  const solutionsVisibleAtUtcMilli = getSolutionVisibilityTimestamp(
    endTimeUtcMilli,
    homeworkInterval,
  );
  const preLectureSolutionsVisibleAtUtcMilli = getSolutionVisibilityTimestamp(
    preLectureEndTimeUtcMilli,
    preLectureInterval,
  );
  const isHomeworkClosedByFlag =
    endTimeUtcMilli === undefined &&
    (normalizeBoolean(component.is_complete ?? component.isComplete) ?? false);
  const isPreLectureClosedByFlag =
    preLectureEndTimeUtcMilli === undefined &&
    (normalizeBoolean(
      component.is_pre_lecture_complete ?? component.isPreLectureComplete,
    ) ?? false);

  return {
    endTimeUtcMilli,
    isLive: timeline.isHomeworkVisible,
    isPastDue: timeline.isHomeworkClosed,
    isPreLecturePastDue: timeline.isPreLectureClosed,
    preLectureEndTimeUtcMilli,
    preLectureSolutionsVisible: areTimelineSolutionsVisible(
      now,
      preLectureEndTimeUtcMilli ?? null,
      isPreLectureClosedByFlag,
      preLectureInterval,
    ),
    preLectureSolutionsVisibleAtUtcMilli,
    solutionsVisible: areTimelineSolutionsVisible(
      now,
      endTimeUtcMilli ?? null,
      isHomeworkClosedByFlag,
      homeworkInterval,
    ),
    solutionsVisibleAtUtcMilli,
  };
}
